import { PDFDocument, rgb, StandardFonts, PDFPage, PDFFont } from 'pdf-lib';
import html2canvas from 'html2canvas';

/* ─────────────── Types ─────────────── */

export interface PdfReportData {
  /** Ranked candidates (sorted by score desc) */
  rankings: {
    resumeFilename: string;
    score: number;
    rank: number;
    strengths?: string[];
    improvements?: string[];
    explanation?: string;
  }[];
  /** Executive summary text */
  summary?: string;
  /** Full job description text */
  jobDescriptionText?: string;
  /** Short JD title / role name */
  jobTitle?: string;
  /** Processing time in ms */
  processingTime?: number;
  /** Total candidates searched before top-N cut */
  totalCandidates?: number;
}

/* ─────────────── Constants ─────────────── */

const A4_W = 595.28; // portrait width in pt
const A4_H = 841.89; // portrait height in pt
const MARGIN = 40;
const USABLE_W = A4_W - MARGIN * 2;
const LINE_H = 14; // body line height
const HEADING_H = 22;
const SECTION_GAP = 16;

const BRAND_ORANGE = rgb(0.976, 0.451, 0.086); // #F97316
const TEXT_DARK = rgb(0.15, 0.15, 0.15);
const TEXT_MED = rgb(0.35, 0.35, 0.35);
const TEXT_LIGHT = rgb(0.52, 0.52, 0.52);
const DIVIDER_COLOR = rgb(0.85, 0.85, 0.85);

/**
 * Sanitize text to only contain characters encodable by WinAnsi (Latin-1 subset
 * used by pdf-lib StandardFonts).  Replaces common Unicode with ASCII equivalents
 * and strips anything else.
 */
function sanitize(text: string): string {
  return text
    // Common punctuation replacements
    .replace(/[\u2018\u2019\u201A]/g, "'")   // curly single quotes
    .replace(/[\u201C\u201D\u201E]/g, '"')   // curly double quotes
    .replace(/\u2026/g, '...')               // ellipsis
    .replace(/[\u2013\u2014]/g, '-')         // en/em dash
    .replace(/\u2022/g, '*')                 // bullet
    .replace(/\u2713|\u2714/g, '+')          // check marks
    .replace(/\u2192|\u2794|\u27A1/g, '>')   // arrows
    .replace(/[\u2010-\u2015]/g, '-')        // various hyphens
    .replace(/\u00A0/g, ' ')                 // non-breaking space
    // Strip any remaining non-WinAnsi chars (keep 0x20-0x7E + 0xA0-0xFF)
    .replace(/[^\x20-\x7E\xA0-\xFF\n\r\t]/g, '');
}

/* ─────────────── Helper: paginated text writer ─────────────── */

interface Writer {
  pdf: PDFDocument;
  page: PDFPage;
  y: number;
  fontBody: PDFFont;
  fontBold: PDFFont;
}

function ensureSpace(w: Writer, needed: number): void {
  if (w.y - needed < MARGIN) {
    w.page = w.pdf.addPage([A4_W, A4_H]);
    w.y = A4_H - MARGIN;
  }
}

/** Word-wrap text and draw, returning new y */
function drawWrapped(
  w: Writer,
  text: string,
  font: PDFFont,
  fontSize: number,
  color: ReturnType<typeof rgb>,
  lineH: number,
  maxLines: number = 999,
): number {
  const words = text.split(/\s+/);
  let line = '';
  let linesDrawn = 0;

  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word;
    const testW = font.widthOfTextAtSize(testLine, fontSize);
    if (testW > USABLE_W && line) {
      ensureSpace(w, lineH);
      w.page.drawText(line, { x: MARGIN, y: w.y, size: fontSize, font, color });
      w.y -= lineH;
      linesDrawn++;
      if (linesDrawn >= maxLines) {
        // append ellipsis to last drawn line
        ensureSpace(w, lineH);
        w.page.drawText('...', { x: MARGIN, y: w.y, size: fontSize, font, color: TEXT_LIGHT });
        w.y -= lineH;
        return w.y;
      }
      line = word;
    } else {
      line = testLine;
    }
  }
  if (line) {
    ensureSpace(w, lineH);
    w.page.drawText(line, { x: MARGIN, y: w.y, size: fontSize, font, color });
    w.y -= lineH;
  }
  return w.y;
}

function drawDivider(w: Writer): void {
  ensureSpace(w, 8);
  w.y -= 4;
  w.page.drawLine({
    start: { x: MARGIN, y: w.y },
    end: { x: A4_W - MARGIN, y: w.y },
    thickness: 0.5,
    color: DIVIDER_COLOR,
  });
  w.y -= 8;
}

function drawSectionHeading(w: Writer, text: string): void {
  ensureSpace(w, HEADING_H + 4);
  w.page.drawText(text, {
    x: MARGIN,
    y: w.y,
    size: 13,
    font: w.fontBold,
    color: BRAND_ORANGE,
  });
  w.y -= HEADING_H;
}

/* ─────────────── Main export function ─────────────── */

/**
 * Build a professional PDF report and trigger download.
 *
 * @param data - Structured report data (rankings, JD text, summary, etc.)
 * @param filename - Download filename without extension.
 * @param chartElement - Optional DOM element of the chart to capture as image.
 */
export async function exportReportPdf(
  data: PdfReportData,
  filename: string,
  chartElement?: HTMLElement | null,
): Promise<void> {
  const pdf = await PDFDocument.create();
  const fontBody = await pdf.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);

  let page = pdf.addPage([A4_W, A4_H]);
  let y = A4_H - MARGIN;

  const w: Writer = { pdf, page, y, fontBody, fontBold };

  /* ── 1. Report Header ── */
  // Brand bar at very top
  page.drawRectangle({
    x: 0, y: A4_H - 6, width: A4_W, height: 6,
    color: BRAND_ORANGE,
  });

  w.y = A4_H - MARGIN - 10;

  // Title
  page.drawText('Resume Ranking Report', {
    x: MARGIN, y: w.y, size: 22, font: fontBold, color: TEXT_DARK,
  });
  w.y -= 28;

  // Attribution + date
  const dateStr = new Date().toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  page.drawText('Prepared by xRank from Aram AI Labs', {
    x: MARGIN, y: w.y, size: 9, font: fontBody, color: TEXT_LIGHT,
  });
  page.drawText(dateStr, {
    x: A4_W - MARGIN - fontBody.widthOfTextAtSize(dateStr, 9),
    y: w.y, size: 9, font: fontBody, color: TEXT_LIGHT,
  });
  w.y -= 16;

  // Job title if available
  if (data.jobTitle) {
    page.drawText(sanitize(`Role: ${data.jobTitle}`), {
      x: MARGIN, y: w.y, size: 11, font: fontBold, color: TEXT_MED,
    });
    w.y -= 18;
  }

  // Quick stats line
  const topScore = data.rankings[0]?.score?.toFixed(1) || 'N/A';
  const avgScore = data.rankings.length
    ? Math.round(data.rankings.reduce((s, r) => s + r.score, 0) / data.rankings.length)
    : 0;
  const statsLine = sanitize(`${data.rankings.length} candidates ranked  |  Top score: ${topScore}%  |  Average: ${avgScore}%`
    + (data.processingTime ? `  |  ${data.processingTime}ms` : ''));
  page.drawText(statsLine, {
    x: MARGIN, y: w.y, size: 9, font: fontBody, color: TEXT_MED,
  });
  w.y -= 14;

  drawDivider(w);

  /* ── 2. Job Description ── */
  if (data.jobDescriptionText && data.jobDescriptionText.trim()) {
    drawSectionHeading(w, 'Job Description');
    const jdPreview = sanitize(data.jobDescriptionText.trim());
    // Show up to ~40 lines worth of JD text
    drawWrapped(w, jdPreview, fontBody, 9, TEXT_MED, 12, 40);
    w.y -= SECTION_GAP;
    drawDivider(w);
  }

  /* ── 3. Executive Summary ── */
  if (data.summary && data.summary.trim()) {
    drawSectionHeading(w, 'Executive Summary');
    drawWrapped(w, sanitize(data.summary.trim()), fontBody, 10, TEXT_DARK, LINE_H, 30);
    w.y -= SECTION_GAP;
    drawDivider(w);
  }

  /* ── 4. Chart (captured from DOM) ── */
  if (chartElement) {
    try {
      const canvas = await html2canvas(chartElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgBytes = await new Promise<Uint8Array>((resolve) => {
        canvas.toBlob((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
          reader.readAsArrayBuffer(blob!);
        }, 'image/png');
      });

      const pngImage = await pdf.embedPng(imgBytes);
      const imgW = USABLE_W;
      const imgH = (canvas.height / canvas.width) * imgW;
      const cappedH = Math.min(imgH, 260); // cap chart height

      ensureSpace(w, cappedH + HEADING_H + 8);
      drawSectionHeading(w, 'Score Overview');
      w.page.drawImage(pngImage, {
        x: MARGIN, y: w.y - cappedH,
        width: imgW, height: cappedH,
      });
      w.y -= cappedH + SECTION_GAP;
      drawDivider(w);
    } catch (err) {
      console.warn('Chart capture failed, skipping chart in PDF', err);
    }
  }

  /* ── 5. Detailed Candidate Cards ── */
  drawSectionHeading(w, 'Detailed Candidate Analysis');

  const sorted = [...data.rankings].sort((a, b) => b.score - a.score);

  for (const candidate of sorted) {
    // Candidate header: name + score
    const name = candidate.resumeFilename
      ?.split('/').pop()
      ?.replace(/^default_user_\d+_/, '')
      ?.replace(/\.[^/.]+$/, '') || candidate.resumeFilename;
    const scoreStr = `${candidate.score?.toFixed(1)}%  (#${candidate.rank})`;

    ensureSpace(w, 40);

    // Name
    w.page.drawText(sanitize(name), {
      x: MARGIN + 4, y: w.y, size: 11, font: fontBold, color: TEXT_DARK,
    });
    // Score on right
    w.page.drawText(scoreStr, {
      x: A4_W - MARGIN - fontBold.widthOfTextAtSize(scoreStr, 11),
      y: w.y, size: 11, font: fontBold, color: BRAND_ORANGE,
    });
    w.y -= 18;

    // Strengths
    if (candidate.strengths && candidate.strengths.length > 0) {
      ensureSpace(w, LINE_H);
      w.page.drawText('Strengths:', {
        x: MARGIN + 8, y: w.y, size: 9, font: fontBold, color: TEXT_MED,
      });
      w.y -= 13;
      for (const s of candidate.strengths.slice(0, 3)) {
        ensureSpace(w, 12);
        drawWrapped(
          w,
          sanitize(`  +  ${s}`),
          fontBody, 9, TEXT_MED, 12, 3
        );
      }
    }

    // Improvements
    if (candidate.improvements && candidate.improvements.length > 0) {
      ensureSpace(w, LINE_H);
      w.page.drawText('Areas for Improvement:', {
        x: MARGIN + 8, y: w.y, size: 9, font: fontBold, color: TEXT_MED,
      });
      w.y -= 13;
      for (const s of candidate.improvements.slice(0, 3)) {
        ensureSpace(w, 12);
        drawWrapped(
          w,
          sanitize(`  >  ${s}`),
          fontBody, 9, TEXT_MED, 12, 3
        );
      }
    }

    // Explanation
    if (candidate.explanation) {
      ensureSpace(w, LINE_H);
      w.page.drawText('Analysis:', {
        x: MARGIN + 8, y: w.y, size: 9, font: fontBold, color: TEXT_MED,
      });
      w.y -= 13;
      drawWrapped(w, sanitize(`  ${candidate.explanation}`), fontBody, 9, TEXT_MED, 12, 6);
    }

    w.y -= 8;
    // Light separator between candidates
    ensureSpace(w, 4);
    w.page.drawLine({
      start: { x: MARGIN + 20, y: w.y },
      end: { x: A4_W - MARGIN - 20, y: w.y },
      thickness: 0.3,
      color: DIVIDER_COLOR,
    });
    w.y -= 10;
  }

  /* ── 6. Footer on every page ── */
  const pages = pdf.getPages();
  for (let i = 0; i < pages.length; i++) {
    const p = pages[i];
    const footerText = `xRank from Aram AI Labs  |  Page ${i + 1} of ${pages.length}`;
    p.drawText(footerText, {
      x: MARGIN,
      y: 18,
      size: 7,
      font: fontBody,
      color: TEXT_LIGHT,
    });
    // Bottom brand line
    p.drawRectangle({
      x: 0, y: 0, width: A4_W, height: 3,
      color: BRAND_ORANGE,
    });
  }

  /* ── Save & download ── */
  const pdfBytes = await pdf.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

/* ─── Legacy wrapper (kept for backward compat, unused) ─── */
export async function exportElementToPdf(
  element: HTMLElement,
  filename: string,
  _title?: string,
): Promise<void> {
  // Fallback: just screenshot the element
  const { PDFDocument: PD } = await import('pdf-lib');
  const canvas = await html2canvas(element, {
    scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 1200,
  });
  const imgBytes = await new Promise<Uint8Array>((res) => {
    canvas.toBlob((b) => {
      const r = new FileReader();
      r.onloadend = () => res(new Uint8Array(r.result as ArrayBuffer));
      r.readAsArrayBuffer(b!);
    }, 'image/png');
  });
  const pdf = await PD.create();
  const img = await pdf.embedPng(imgBytes);
  const imgW = USABLE_W;
  const imgH = (canvas.height / canvas.width) * imgW;
  const page = pdf.addPage([A4_W, Math.max(A4_H, imgH + MARGIN * 2)]);
  page.drawImage(img, { x: MARGIN, y: page.getHeight() - MARGIN - imgH, width: imgW, height: imgH });
  const bytes = await pdf.save();
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const u = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = u; a.download = `${filename}.pdf`; a.click();
  URL.revokeObjectURL(u);
}
